import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { X } from "lucide-react";

const schema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
});

export interface SplitTestPopupRef {
  openManual: () => void;
}

interface SplitTestPopupProps {
  onSubscribed?: () => void;
}

export const SplitTestPopup = forwardRef<SplitTestPopupRef, SplitTestPopupProps>(({ onSubscribed }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [testGroup, setTestGroup] = useState<'A' | 'B' | 'C' | null>(null);
  const [visitorId] = useState(() => Math.random().toString(36).substring(7));
  const { toast } = useToast();

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: "", lastName: "", email: "" },
  });

  useImperativeHandle(ref, () => ({
    openManual: () => {
      setTestGroup('C');
      setIsOpen(true);
      recordEvent('view', 'C');
    }
  }));

  useEffect(() => {
    // Only auto-assign A or B if not manually triggered as C
    const storedGroup = localStorage.getItem("split_test_group") as 'A' | 'B';
    const group = storedGroup || (Math.random() < 0.5 ? 'A' : 'B');
    if (!storedGroup) localStorage.setItem("split_test_group", group);
    setTestGroup(group);

    // Test A: Every 3rd visit (simulated by localStorage counter), after 5s
    if (group === 'A') {
      const visitCount = parseInt(localStorage.getItem("visit_count") || "0") + 1;
      localStorage.setItem("visit_count", visitCount.toString());
      if (visitCount % 3 === 0) {
        const timer = setTimeout(() => {
          setIsOpen(true);
          recordEvent('view', 'A');
        }, 5000);
        return () => clearTimeout(timer);
      }
    }

    // Test B: Every visit, exit intent
    if (group === 'B') {
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 0 && !localStorage.getItem("popup_shown_B")) {
          setIsOpen(true);
          localStorage.setItem("popup_shown_B", "true");
          recordEvent('view', 'B');
        }
      };
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => document.removeEventListener("mouseleave", handleMouseLeave);
    }
  }, []);

  const recordEvent = async (event: 'view' | 'conversion', group: 'A' | 'B' | 'C') => {
    try {
      await apiRequest("POST", "/api/split-test/event", {
        testGroup: group,
        event,
        visitorId,
      });
    } catch (e) {
      console.error("Failed to record split test event", e);
    }
  };

  const onSubmit = async (data: z.infer<typeof schema>) => {
    try {
      await apiRequest('/api/mailchimp/subscribe', {
        method: 'POST',
        body: JSON.stringify({ ...data, audienceId: 'a213512fa7' })
      });
      toast({ title: "Welcome!", description: "You've successfully joined the list." });
      setIsOpen(false);
      if (testGroup) recordEvent('conversion', testGroup);
      onSubscribed?.();
    } catch (e) {
      toast({ title: "Error", description: "Failed to subscribe.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px] bg-gothic-charcoal border-blood-red text-gothic-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-playfair text-blood-red">Enter the Reader Vault</DialogTitle>
          <DialogDescription className="text-gray-300">
            Join and receive a free copy of Skin and Bones — plus exclusive updates, sneak peeks, and gripping thriller tales delivered straight to your inbox.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="First Name" {...field} className="bg-white border-gray-700 text-black placeholder:text-gray-500 font-medium" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Last Name" {...field} className="bg-white border-gray-700 text-black placeholder:text-gray-500 font-medium" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Email Address" {...field} className="bg-white border-gray-700 text-black placeholder:text-gray-500 font-medium" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full bg-blood-red hover:bg-red-700 text-white font-bold py-6 uppercase tracking-wider">
              JOIN THE MAILING LIST
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
